import { queries } from "@/graphql";
import { useQuery } from "@apollo/client/react";
import { useEffect, useMemo } from "react";
import { enqueueSnackbar } from "notistack";
import type { DataLimitType, QueryDataLimitListArgs } from "@/types/graphql";

/**
 * Custom hook to fetch the list of modules.
 * @param {object} props - The properties to be passed to the query.
 * @returns {{dataLimitList: Array<{id: string, name: string}>, loading: boolean, error: object}} - The list of modules, loading state, and error.
 */
export function useDataLimitList(props: QueryDataLimitListArgs = {}) {
  const propsKey = JSON.stringify(props);
  const variables: QueryDataLimitListArgs = useMemo(
    () => ({ ...props }),
    [propsKey],
  );

  const { data, loading, error } = useQuery<
    {
      dataLimitList: Array<DataLimitType | null> | null;
    },
    QueryDataLimitListArgs
  >(queries.dataLimitList, {
    variables,
    errorPolicy: "all",
  });

  // Handle error
  useEffect(() => {
    if (error) {
      enqueueSnackbar(error.message, {
        variant: "error",
      });
    }
  }, [error]);

  const dataLimitList = useMemo(
    () =>
      (data?.dataLimitList || []).filter(
        (dataLimit): dataLimit is DataLimitType => Boolean(dataLimit),
      ),
    [data?.dataLimitList],
  );

  return {
    dataLimitList,
    loading,
    error,
  };
}
